import React from 'react';

/* W-025 -- AN UNEXPECTED ERROR WHILE DRAWING A PAGE SHOWS A READABLE MESSAGE, NOT A BLANK WHITE PAGE.
 *
 * Before this component there was NO error boundary anywhere in client/src, and React 19 unmounts the
 * whole root on an uncaught render error: the sidebar, the company switcher and the page all vanish.
 * One such fault (the 09-16 quarterly view) stayed a white page for 134 days because nobody could
 * see what had happened.
 *
 * ONE implementation, used three ways by App.js (Principle 0):
 *   - around EACH ROUTED PAGE: the navigation and the switcher are siblings of <Routes>, so they stay
 *     mounted and usable while only the page slot shows this message;
 *   - around the model provider: a throw while the provider renders (the balance-sheet assumptions
 *     re-run runs inside a state updater, i.e. during render) is caught with the navigation kept;
 *   - once at the very top, as the last resort for a fault in the shell itself.
 *
 * WHAT IT CANNOT CATCH (React's rule, not ours): errors in event handlers, in async code (a rejected
 * fetch, anything after an await) and in timers. ModelProvider's load already catches its own model
 * failures and shows ModelLoadError; the sweep-settings re-run (an event handler) is filed separately.
 *
 * RESET ON NAVIGATION: `resetKey` (the route) clears the error when it changes, WITHOUT remounting the
 * children -- a remount of the provider-level boundary would refetch the whole model on every click.
 *
 * LOG: console.error with the same details the operator can copy. No network call, no telemetry.
 * Display-only: no engine call, no stored value, no number.
 */

const FONT = "'DM Sans',-apple-system,sans-serif";
const MONO = "'Space Mono',monospace";

/* THE COPYABLE DETAILS BLOCK -- shared with ModelLoadError's "could not be calculated" message, so the two
 * say the same thing in the same shape (Principle 0: one block, two readers). */
export function detailsText({ page, company, at, message }) {
  const lines = [];
  lines.push('Page: ' + (page || 'unknown'));
  if (company) lines.push('Company: ' + company);
  lines.push('Time: ' + at);
  lines.push('Error: ' + message);
  return lines.join('\n');
}

export function copyText(text, onCopied) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onCopied, () => {});
    }
  } catch (e) { /* the block is selectable, so copying by hand still works */ }
}

export const detailsButtonStyle = {
  fontFamily: FONT, fontSize: 13, padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid #D5D8E3', background: '#fff', color: '#1A1C2E',
};
export const detailsPrimaryButtonStyle = { ...detailsButtonStyle, background: '#2A6DB0', borderColor: '#2A6DB0', color: '#fff' };

export function DetailsBlock({ text }) {
  return (
    <pre style={{
      marginTop: 18, padding: '10px 14px', maxWidth: 560, width: '100%', boxSizing: 'border-box',
      textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word', userSelect: 'all',
      fontFamily: MONO, fontSize: 11, color: '#5A5E73', background: '#F7F8FB',
      border: '1px solid #E2E5F0', borderRadius: 6,
    }}>{text}</pre>
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: '', at: null, resetKey: props.resetKey, showDetails: false, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error, at: new Date().toISOString(), showDetails: false, copied: false };
  }

  // A new route clears the message before the new page renders (no flash of the old error).
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) {
      return { resetKey: props.resetKey, error: null, componentStack: '', at: null, showDetails: false, copied: false };
    }
    return null;
  }

  componentDidCatch(error, info) {
    const componentStack = (info && info.componentStack) || '';
    this.setState({ componentStack });
    console.error('[FinModel] A page failed to draw.', {
      page: this.props.page || null,
      company: this.props.tenant || null,
      at: this.state.at || new Date().toISOString(),
      message: error && error.message ? error.message : String(error),
      stack: error && error.stack ? error.stack : null,
      componentStack,
    });
  }

  summary() {
    const { error, at } = this.state;
    return detailsText({ page: this.props.page, company: this.props.tenant, at,
      message: error && error.message ? error.message : String(error) });
  }

  copy = () => copyText(this.summary(), () => this.setState({ copied: true }));

  render() {
    if (!this.state.error) return this.props.children;
    const { error, componentStack, showDetails, copied } = this.state;
    return (
      <div role="alert" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', padding: '60px 24px', textAlign: 'center', fontFamily: FONT,
      }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>{'\u26A0'}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1A1C2E', marginBottom: 12 }}>
          Something went wrong showing this page.
        </div>
        <div style={{ fontSize: 14, color: '#5A5E73', maxWidth: 560, lineHeight: 1.6 }}>
          Your data has not been changed. Reload the page to try again, or choose another page from the
          menu. If it keeps happening, send the details below to support.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button type="button" style={detailsPrimaryButtonStyle}
            onClick={() => window.location.reload()}>Reload</button>
          <button type="button" style={detailsButtonStyle} onClick={this.copy}>{copied ? 'Copied' : 'Copy details'}</button>
        </div>
        <DetailsBlock text={this.summary()} />
        <button type="button" onClick={() => this.setState({ showDetails: !showDetails })}
          style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 12, color: '#8B8FA3' }}>
          {showDetails ? 'Hide technical details' : 'Show technical details'}
        </button>
        {showDetails ? (
          <pre style={{
            marginTop: 6, padding: '10px 14px', maxWidth: 560, width: '100%', boxSizing: 'border-box', maxHeight: 240, overflow: 'auto',
            textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: MONO, fontSize: 10, color: '#8B8FA3',
            background: '#F7F8FB', border: '1px solid #E2E5F0', borderRadius: 6,
          }}>{(error && error.stack ? error.stack : String(error)) + (componentStack ? '\n' + componentStack : '')}</pre>
        ) : null}
      </div>
    );
  }
}
